import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  Button
} from '@mui/material';
import {
  ArrowDownward as DecreaseIcon,
  ArrowUpward as IncreaseIcon,
  SwapHoriz as TransferIcon,
  Settings as AdjustmentIcon
} from '@mui/icons-material';
import { getProductInventoryTransactions } from '../../services/inventoryService';
import { InventoryTransaction } from '../../types/inventory';
import { formatDateTime } from '../../utils/dateUtils';

interface ProductInventoryHistoryProps {
  productId: string;
  variantId?: string;
  title?: string;
}

const ProductInventoryHistory: React.FC<ProductInventoryHistoryProps> = ({ 
  productId, 
  variantId,
  title = "سجل حركة المخزون"
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchTransactions();
  }, [productId, variantId, page]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getProductInventoryTransactions(productId, variantId, page);
      
      if (response.success) {
        setTransactions(response.transactions || []);
        setTotalPages(response.totalPages || 1);
      } else {
        setError('فشل في تحميل سجل حركة المخزون');
      }
    } catch (err) {
      console.error('Error fetching inventory transactions:', err);
      setError('حدث خطأ أثناء تحميل سجل حركة المخزون');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'addition':
        return <IncreaseIcon fontSize="small" color="success" />;
      case 'subtraction':
        return <DecreaseIcon fontSize="small" color="error" />;
      case 'transfer':
      case 'transfer_in':
      case 'transfer_out':
        return <TransferIcon fontSize="small" color="primary" />;
      case 'adjustment':
        return <AdjustmentIcon fontSize="small" color="warning" />;
      default:
        return null;
    }
  };

  const getTransactionTypeText = (type: string) => {
    switch (type) {
      case 'addition':
        return 'إضافة';
      case 'subtraction':
        return 'خصم';
      case 'transfer':
        return 'تحويل';
      case 'transfer_in':
        return 'وارد تحويل';
      case 'transfer_out':
        return 'صادر تحويل';
      case 'adjustment':
        return 'تعديل';
      default:
        return type;
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper elevation={2} sx={{ p: 2, mb: 3, direction: 'rtl' }}>
        <Typography variant="h6" component="h3" gutterBottom>
          {title}
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={40} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && transactions.length === 0 && (
          <Alert severity="info">
            لا توجد حركات مخزون مسجلة لهذا المنتج
          </Alert>
        )}

        {!loading && !error && transactions.length > 0 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>نوع الحركة</TableCell>
                    <TableCell>الكمية</TableCell>
                    <TableCell>المستودع</TableCell>
                    <TableCell>المرجع</TableCell>
                    <TableCell>ملاحظات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction._id}>
                      <TableCell>{formatDateTime(transaction.transactionDate)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTransactionIcon(transaction.type)}
                          {getTransactionTypeText(transaction.type)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {transaction.type === 'addition' ? '+' : 
                             transaction.type === 'subtraction' ? '-' : ''}
                            {transaction.quantity} {transaction.unitName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({transaction.previousQuantity} → {transaction.newQuantity})
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{transaction.warehouseName}</TableCell>
                      <TableCell>
                        {transaction.referenceNumber ? (
                          <Chip 
                            size="small" 
                            label={`${transaction.referenceModel === 'Invoice' ? 'فاتورة' : 'مستند'}: ${transaction.referenceNumber}`} 
                            color="primary" 
                            variant="outlined"
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>{transaction.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button 
                  onClick={handlePreviousPage} 
                  disabled={page === 1}
                  sx={{ mx: 1 }}
                >
                  السابق
                </Button>
                <Typography sx={{ alignSelf: 'center', mx: 2 }}>
                  صفحة {page} من {totalPages}
                </Typography>
                <Button 
                  onClick={handleNextPage} 
                  disabled={page === totalPages}
                  sx={{ mx: 1 }}
                >
                  التالي
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProductInventoryHistory;